import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  CabecalhoOrdenavel,
  compara,
  proximaOrdem,
  type Ordem,
} from '@/components/CabecalhoOrdenavel';
import { buscaContatos, type ContatoAdmin } from '@/features/notas/service';
import { dataBr, nomeProprio } from '@/features/notas/texto';
import { useAsync } from '@/hooks/useAsync';
import { formataCelular } from '@/features/notas/Ofertas';

const ORIGEM = {
  login: {
    rotulo: 'login',
    cor: 'success' as const,
    dica: 'Conta Google do painel (verificada e ligada ao RA)',
  },
  professor: {
    rotulo: 'professor',
    cor: 'secondary' as const,
    dica: 'Informado pelo aluno direto ao professor',
  },
  formulario: {
    rotulo: 'formulário',
    cor: 'primary' as const,
    dica: 'Quem enviou o formulário de entrega',
  },
  aws: { rotulo: 'AWS', cor: 'default' as const, dica: 'Conta do AWS Academy' },
};

type Campo = 'nome' | 'pessoais' | 'cps' | 'celular' | 'ofertas';
const PESO_ORIGEM = { login: 4, professor: 3, formulario: 2, aws: 1 };
/** "PDMT 20261" → "PDMT 2026/1" */
const disciplinaSemestre = (s: string) => s.replace(/(\d{4})(\d)$/, '$1/$2');

/** Valor usado para ordenar cada coluna (null = vai para o fim). */
function chave(c: ContatoAdmin, campo: Campo): string | number | null {
  switch (campo) {
    case 'nome':
      return c.nome;
    case 'pessoais':
      // melhor origem disponível (login > professor > formulário > AWS) e, no empate, quantos endereços
      return c.pessoais.length
        ? Math.max(...c.pessoais.map((p) => PESO_ORIGEM[p.origem])) * 100 + c.pessoais.length
        : null;
    case 'cps':
      return c.cps;
    case 'celular':
      // número visível (contato ligado) > desligado pelo aluno > sem número
      return c.celular ? 2 : c.temCelular ? 1 : null;
    case 'ofertas':
      return c.ofertas ? Date.parse(c.ofertas_em ?? '') || 1 : null;
  }
}

const semAcento = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

function baixaCsv(lista: ContatoAdmin[]) {
  const q = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const cab = [
    'RA',
    'Nome',
    'E-mail preferido',
    'E-mail @cps',
    'Celular (contato ligado)',
    'Aceita ofertas',
    'Aceite em',
    'Outros e-mails (origem)',
    'Disciplinas',
    'Link para sair',
  ];
  const linhas = lista.map((c) => [
    c.ra,
    c.nome,
    c.preferido,
    c.cps,
    c.celular ? formataCelular(c.celular) : '',
    c.ofertas ? 'sim' : 'não',
    c.ofertas_em ?? '',
    c.pessoais.map((p) => `${p.email} (${p.origem})`).join('; '),
    c.disciplinas.map(disciplinaSemestre).join(', '),
    `${c.linkPreferencias}&acao=sair`,
  ]);
  const csv = '﻿' + [cab, ...linhas].map((l) => l.map(q).join(';')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  a.download = `contatos-alunos-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** E-mails de cada aluno (de onde vieram) e quem aceita receber ofertas de conteúdo. */
export function ContatosAba() {
  const { data, error } = useAsync((s) => buscaContatos(s), []);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<'todos' | 'aceitam' | 'sem-email'>('todos');
  const [ordem, setOrdem] = useState<Ordem<Campo>>({ campo: 'nome', dir: 'asc' });
  // nome começa de A a Z; as outras colunas começam pelo "melhor" (mais e-mails, quem aceitou)
  const ordena = (campo: Campo) =>
    setOrdem((o) => proximaOrdem(o, campo, campo === 'nome' || campo === 'cps' ? 'asc' : 'desc'));

  const lista = useMemo(() => {
    const q = semAcento(busca.trim());
    return (data ?? [])
      .filter(
        (c) =>
          (filtro === 'todos' || (filtro === 'aceitam' ? c.ofertas : !c.preferido)) &&
          (!q ||
            semAcento(
              `${c.nome} ${c.ra} ${c.preferido ?? ''} ${c.pessoais.map((p) => p.email).join(' ')}`,
            ).includes(q)),
      )
      .sort(
        (x, y) =>
          compara(chave(x, ordem.campo), chave(y, ordem.campo), ordem.dir) ||
          compara(x.nome, y.nome, 'asc'),
      );
  }, [data, busca, filtro, ordem]);

  if (error) return <Alert severity="error">Não consegui carregar os contatos.</Alert>;
  const total = data?.length ?? 0;
  const aceitam = data?.filter((c) => c.ofertas).length ?? 0;
  const comEmail = data?.filter((c) => c.preferido).length ?? 0;

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        {comEmail} de {total} alunos têm e-mail pessoal;{' '}
        {data?.filter((c) => c.celular).length ?? 0} deixaram o celular para contato (login no
        painel, formulário de entrega ou AWS Academy). {aceitam} aceitam receber ofertas de
        conteúdo. Só mande ofertas para quem aceitou e sempre com o link para sair (vem na
        planilha).
      </Alert>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'center' } }}
      >
        <TextField
          size="small"
          placeholder="Buscar por nome, RA ou e-mail"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v) => v && setFiltro(v)}
        >
          <ToggleButton value="todos">Todos</ToggleButton>
          <ToggleButton value="aceitam">Aceitam ofertas</ToggleButton>
          <ToggleButton value="sem-email">Sem e-mail pessoal</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          startIcon={<DownloadRounded />}
          disabled={!lista.length}
          onClick={() => baixaCsv(lista)}
        >
          Baixar planilha ({lista.length})
        </Button>
      </Stack>
      <Card>
        <TableContainer sx={{ maxHeight: 700 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <CabecalhoOrdenavel campo="nome" ordem={ordem} onOrdena={ordena}>
                  Aluno
                </CabecalhoOrdenavel>
                <CabecalhoOrdenavel campo="pessoais" ordem={ordem} onOrdena={ordena}>
                  E-mails pessoais (origem)
                </CabecalhoOrdenavel>
                <CabecalhoOrdenavel campo="cps" ordem={ordem} onOrdena={ordena}>
                  @cps
                </CabecalhoOrdenavel>
                <CabecalhoOrdenavel campo="celular" ordem={ordem} onOrdena={ordena}>
                  Celular
                </CabecalhoOrdenavel>
                <CabecalhoOrdenavel campo="ofertas" ordem={ordem} onOrdena={ordena}>
                  Ofertas
                </CabecalhoOrdenavel>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((c) => (
                <TableRow key={c.ra} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {nomeProprio(c.nome)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      RA {c.ra} · {c.disciplinas.map(disciplinaSemestre).join(', ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {c.pessoais.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          –
                        </Typography>
                      )}
                      {c.pessoais.map((p) => (
                        <Stack
                          key={p.email + p.origem}
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center' }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: p.email === c.preferido ? 700 : 400 }}
                          >
                            {p.email}
                          </Typography>
                          <Tooltip
                            title={`${ORIGEM[p.origem].dica}${p.detalhe ? ` · ${p.detalhe}` : ''}`}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              color={ORIGEM[p.origem].cor}
                              label={ORIGEM[p.origem].rotulo}
                            />
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {c.cps ??
                      (c.emails_cps.length > 1
                        ? `${c.emails_cps.length} possíveis (homônimos)`
                        : '–')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {c.celular ? (
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{formataCelular(c.celular)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          contato ligado
                        </Typography>
                      </Stack>
                    ) : c.temCelular ? (
                      <Tooltip title="O aluno informou um número, mas desligou o contato: o número fica oculto.">
                        <Chip size="small" variant="outlined" label="Desligado pelo aluno" />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        –
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.ofertas ? (
                      <Chip
                        size="small"
                        color="success"
                        label={`Sim · desde ${dataBr(c.ofertas_em)}`}
                      />
                    ) : (
                      <Chip size="small" variant="outlined" label="Não" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
}
